import { Switch, Route } from "wouter";
import { TooltipProvider } from "@/components/ui/tooltip.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.jsx";
import { AssetProvider } from "./contexts/AssetContext.jsx";
import { DowntimeProvider } from "./contexts/DowntimeContext.jsx";
import { DataLoaderProvider } from "./contexts/DataLoaderContext.jsx";
import { ErrorProvider } from "./contexts/ErrorContext.jsx";
import { TagDataProvider } from "./contexts/TagDataContext.jsx";
import { UniversalDescriptorsProvider } from "./contexts/UniversalDescriptorsContext.jsx";
import {
  SessionTenantProvider,
  useSessionTenant,
} from "./contexts/SessionTenantContext.jsx";
import { EnalysisOptimizationProvider } from "./contexts/EnalysisOptimizationContext.jsx";
import { NoDataStatsProvider } from "./contexts/NoDataStatsContext.jsx";
import { ErrorBanner } from "@/components/ui/error-banner.jsx";
import { AssetDowntimeEnricher } from "@/components/asset-downtime-enricher.jsx";
import TenantChangeWatcher from "@/components/tenant-change-watcher.jsx";
import Dashboard from "@/pages/dashboard.jsx";
import Map from "@/pages/map.jsx";
import DowntimeEvents from "./pages/downtime-overview.jsx";
import NoDataReport from "@/pages/no-data-report.jsx";
import OperatingPoints from "@/pages/operating-points.jsx";
import DowntimeClassification from "@/pages/downtime-classification.jsx";
import AssetOverview from "@/pages/asset-overview.jsx";
import AssetVitals from "@/pages/asset-vitals.jsx";
import AssetTrends from "@/pages/asset-trends.jsx";
import AssetDowntimeAnalysis from "@/pages/asset-downtime-analysis.jsx";
import RootCauseAnalysis from "@/pages/root-cause-analysis.jsx";
import AssetOptimization from "@/pages/asset-optimization.jsx";
import AssetRedirect from "@/pages/asset-redirect.jsx";
import ManageGroups from "@/pages/manage-groups.jsx";
import NotFound from "@/pages/not-found.jsx";
import Unauthorized from "@/pages/unauthorized.jsx";
import OneViewDisabled from "@/pages/oneview-disabled.jsx";
import { LoadingSpinner } from "@/components/ui/loading.jsx";
import { Button } from "@/components/ui/button.jsx";
import { useUserAccess } from "@/hooks/useUserAccess.js";

/**
 * Guards manage-groups route behind CanManageTagGroups permission.
 * @returns {JSX.Element} Authorized page or unauthorized page
 */
function ManageGroupsRoute() {
  const { canManageTagGroups, isLoadingManageTagGroupTenants } =
    useSessionTenant();

  if (isLoadingManageTagGroupTenants) {
    return null;
  }

  if (!canManageTagGroups) {
    return <Unauthorized />;
  }

  return <ManageGroups />;
}

/**
 * Guards classification route behind IsOneViewAdmin permission.
 * @returns {JSX.Element} Authorized page or unauthorized page
 */
function ClassificationRoute() {
  const { isOneViewAdmin, isLoading } = useUserAccess();

  if (isLoading) {
    return null;
  }

  if (!isOneViewAdmin) {
    return <Unauthorized />;
  }

  return <DowntimeClassification />;
}

/**
 * Router component that defines application routes
 * @returns {JSX.Element} Router component with defined routes
 */
function Router() {
  return (
    <Switch>
      <Route path='/' component={Dashboard} />
      <Route path='/map' component={Map} />
      <Route path='/downtime' component={DowntimeEvents} />
      <Route path='/no-data-report' component={NoDataReport} />
      <Route path='/operating-points' component={OperatingPoints} />
      <Route path='/classification' component={ClassificationRoute} />
      <Route path='/manage-groups' component={ManageGroupsRoute} />
      <Route path='/asset/:id/overview' component={AssetOverview} />
      <Route path='/asset/:id/vitals' component={AssetVitals} />
      <Route path='/asset/:id/trends' component={AssetTrends} />
      <Route
        path='/asset/:id/downtime-analysis'
        component={AssetDowntimeAnalysis}
      />
      <Route
        path='/asset/:id/root-cause-analysis'
        component={RootCauseAnalysis}
      />
      <Route path='/asset/:id/optimization' component={AssetOptimization} />
      <Route path='/asset/:id' component={AssetRedirect} />
      <Route path='/unauthorized' component={Unauthorized} />
      <Route component={NotFound} />
    </Switch>
  );
}

/**
 * Session gate: loads tenant session before mounting fleet data providers.
 * @returns {JSX.Element}
 */
function SessionGate() {
  const { isLoading, error, oneViewEnabled, refetch } = useSessionTenant();

  if (isLoading) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center gap-4 bg-background'>
        <LoadingSpinner className='h-10 w-10 border-primary border-b-transparent' />
        <p className='text-sm font-light text-muted-foreground'>
          Loading session...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-4'>
        <p className='text-sm text-destructive text-center max-w-md'>{error}</p>
        <Button type='button' variant='outline' onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!oneViewEnabled) {
    return <OneViewDisabled />;
  }

  return (
    <AssetProvider>
      <DowntimeProvider>
        <AssetDowntimeEnricher />
        <NoDataStatsProvider>
          <TagDataProvider>
            <EnalysisOptimizationProvider>
              <Router />
            </EnalysisOptimizationProvider>
          </TagDataProvider>
        </NoDataStatsProvider>
      </DowntimeProvider>
    </AssetProvider>
  );
}

/**
 * Main application component
 * Sets up global providers and renders the router
 * @returns {JSX.Element} The main application component
 */
function App() {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <ErrorProvider>
          <DataLoaderProvider>
            <UniversalDescriptorsProvider>
              <SessionTenantProvider>
                <TenantChangeWatcher />
                <ErrorBanner />
                <SessionGate />
              </SessionTenantProvider>
            </UniversalDescriptorsProvider>
          </DataLoaderProvider>
        </ErrorProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
